import { useCallback } from 'react';
import { createBridgeChannel } from './createBridgeChannel';
import { EVENT_DRAFT_CHANNEL, } from '../types/eventDraftChannel';
var channel = createBridgeChannel(EVENT_DRAFT_CHANNEL);
/**
 * Emit a draft patch on the cross-app event-composer channel.
 *
 * `origin` should uniquely identify the emitter so it can ignore its own
 * echoes when subscribing (see `onEventDraftPatch` / `useEventDraftPatch`).
 * Prefer `useEmitEventDraftPatch` in React components; this bridge-taking form
 * is for tests holding an explicit bridge.
 */
export function emitEventDraftPatch(bridge, patch, origin) {
    channel.emit(bridge, { patch: patch, origin: origin });
}
/**
 * Subscribe to draft patches on the cross-app event-composer channel. Returns
 * an unsubscribe function. Prefer `useEventDraftPatch` in React components.
 */
export function onEventDraftPatch(bridge, listener) {
    return channel.subscribe(bridge, function (_a) {
        var patch = _a.patch, origin = _a.origin;
        return listener(patch, origin);
    });
}
/**
 * Subscribe to cross-app draft patches from inside a React component. The
 * subscription is set up once per bridge instance and torn down on unmount; the
 * latest `listener` closure runs on every patch, so callers don't need to
 * memoize it.
 *
 * @example
 * useEventDraftPatch(
 *   (patch) => {
 *     if (patch.eventId !== currentEventId) return;
 *     if (patch.startTimeISO) setBlockStart(moment(patch.startTimeISO));
 *     if (patch.endTimeISO) setBlockEnd(moment(patch.endTimeISO));
 *   },
 *   { ignoreOrigin: 'schedule-meeting-rooms' }
 * );
 */
export function useEventDraftPatch(listener, options) {
    var ignoreOrigin = options === null || options === void 0 ? void 0 : options.ignoreOrigin;
    channel.useSubscribe(function (_a) {
        var patch = _a.patch, origin = _a.origin;
        if (ignoreOrigin !== undefined && origin === ignoreOrigin)
            return;
        listener(patch, origin);
    });
}
/**
 * Returns a stable callback that emits a draft patch on the shell bridge.
 * Reads the bridge from context so callers don't thread it manually.
 */
export function useEmitEventDraftPatch() {
    var emit = channel.useEmit();
    return useCallback(function (patch, origin) {
        return emit({ patch: patch, origin: origin });
    }, [emit]);
}
